import { useQuery } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { customFieldsService } from '../api/custom-fields.service';
import { CUSTOM_FIELD_KEYS } from './queryKeys';

/**
 * Fetches custom field values for every visible task and visible custom-field
 * column in one request — the list-view equivalent of `useTaskFieldValues`,
 * which fetches per task. Call once per list/page load with the currently
 * visible `taskIds`/`fieldIds`, not once per `FieldValueCell`.
 *
 * @remarks
 * - Disabled until `activeOrganizationId`, `projectId`, and at least one task
 *   id and one field id are present — an empty list has nothing to batch.
 * - `staleTime` matches `useTaskFieldValues` (2 min).
 */
export function useTaskFieldValuesBatch(projectId: string, taskIds: string[], fieldIds: string[]) {
  const activeOrganizationId = useOrganizationStore((s) => s.activeOrganizationId);

  return useQuery({
    queryKey: CUSTOM_FIELD_KEYS.taskValuesBatch(
      activeOrganizationId ?? '',
      projectId,
      taskIds,
      fieldIds,
    ),
    queryFn: ({ signal }) =>
      customFieldsService.getTaskFieldValuesBatch(projectId, taskIds, fieldIds, signal),
    staleTime: 120_000,
    enabled: !!activeOrganizationId && !!projectId && taskIds.length > 0 && fieldIds.length > 0,
  });
}
